import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ProductCard from "../components/ProductCard";
import { kategoriAdi, useKatalog } from "../data/katalog";
import { MARKA_TANITIMLARI, markaSlug, markaYolu } from "../data/markalar";

const GRUP_ILK = 8; // kategori başına ilk gösterilen ürün

/** Sekme başlığı ve arama motoru açıklaması */
const useSayfaBilgisi = (baslik, aciklama) => {
  useEffect(() => {
    if (!baslik) return;
    const onceki = document.title;
    document.title = baslik;
    const meta = document.querySelector('meta[name="description"]');
    const oncekiAciklama = meta?.getAttribute("content");
    if (aciklama) meta?.setAttribute("content", aciklama);
    return () => {
      document.title = onceki;
      if (meta && oncekiAciklama != null) meta.setAttribute("content", oncekiAciklama);
    };
  }, [baslik, aciklama]);
};

/** Marka adını amblem gibi gösterir (tek marka logosu olmadığı için tipografik) */
const Amblem = ({ ad, buyuk }) => (
  <span
    className={`inline-block rounded-lg bg-ink font-extrabold uppercase tracking-[0.08em] text-white ${
      buyuk ? "px-5 py-3 text-[26px] sm:text-[32px]" : "px-3 py-1.5 text-[14px]"
    }`}
  >
    {ad.toLocaleUpperCase("tr")}
  </span>
);

/** /markalar — tüm markalar, ürün sayısına göre */
export function MarkaDizini() {
  const { t } = useTranslation();
  const { urunler, hazir } = useKatalog();

  const markalar = useMemo(() => {
    const m = new Map();
    for (const u of urunler) {
      if (!u.marka) continue; // markası girilmemiş ürün marka dizininde yer almaz
      if (!m.has(u.marka)) m.set(u.marka, []);
      m.get(u.marka).push(u);
    }
    return [...m.entries()]
      .map(([ad, liste]) => ({ ad, adet: liste.length, gorseller: liste.slice(0, 3).map((u) => u.gorsel) }))
      .sort((a, b) => b.adet - a.adet || a.ad.localeCompare(b.ad, "tr"));
  }, [urunler]);

  useSayfaBilgisi(`${t("brandPage.indexTitle")} — ${t("siteName")}`, t("brandPage.indexMeta"));

  return (
    <section className="container-page section">
      <div className="page-head">
        <p className="eyebrow">{t("brandPage.eyebrow")}</p>
        <h1 className="page-title mt-3">{t("brandPage.indexTitle")}</h1>
        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-body">
          {hazir && t("brandPage.indexText", { marka: markalar.length, urun: urunler.length.toLocaleString("tr") })}
        </p>
      </div>
      <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {markalar.map((m) => (
          <li key={m.ad}>
            <Link
              to={markaYolu(m.ad)}
              className="group flex h-full flex-col overflow-hidden rounded-xl border border-line bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_34px_-12px_rgba(0,0,0,0.22)]"
            >
              <div className="grid h-20 grid-cols-3 grid-rows-1 gap-px overflow-hidden bg-line/60">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="relative min-h-0 overflow-hidden bg-white">
                    {m.gorseller[i] && (
                      <img src={m.gorseller[i]} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-contain p-1.5" />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex flex-1 items-center justify-between gap-2 border-t border-line px-3.5 py-3">
                <span className="min-w-0">
                  <span className="block truncate text-[14px] font-bold uppercase tracking-wide text-ink">
                    {m.ad.toLocaleUpperCase("tr")}
                  </span>
                  <span className="text-[12px] tabular-nums text-muted">{t("home.brandProductCount", { count: m.adet })}</span>
                </span>
                <span aria-hidden="true" className="arrow-forward text-ink/40 transition-colors group-hover:text-accent" />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** /marka/:slug — tek marka: tanıtım, kategori kısayolları ve kategorilere ayrılmış ürünler */
export default function MarkaSayfasi() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "tr").split("-")[0];
  const { urunler, adlar, hazir } = useKatalog();
  const [acik, setAcik] = useState({}); // kategori -> tümü gösteriliyor mu

  const markaUrunleri = useMemo(() => urunler.filter((u) => markaSlug(u.marka) === slug), [urunler, slug]);
  const ad = markaUrunleri[0]?.marka;

  // Kategorilere göre gruplar (en kalabalık önce)
  const gruplar = useMemo(() => {
    const m = new Map();
    for (const u of markaUrunleri) {
      if (!m.has(u.kategori)) m.set(u.kategori, []);
      m.get(u.kategori).push(u);
    }
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [markaUrunleri]);

  const kategoriListesi = gruplar.slice(0, 4).map(([k]) => kategoriAdi(adlar, k, lang));
  const otomatikTanitim = ad
    ? t("brandPage.autoText", { marka: ad, adet: markaUrunleri.length, kategoriler: kategoriListesi.join(", ") })
    : "";
  const tanitim = lang === "tr" && MARKA_TANITIMLARI[slug] ? MARKA_TANITIMLARI[slug] : null;

  useSayfaBilgisi(
    ad ? `${ad} ${t("brandPage.productsWord")} — ${t("siteName")}` : null,
    ad ? t("brandPage.meta", { marka: ad, adet: markaUrunleri.length }) : null
  );

  useEffect(() => setAcik({}), [slug]);

  if (!hazir) {
    return (
      <section className="container-page section">
        <div className="h-40 animate-pulse rounded-2xl bg-canvas" />
      </section>
    );
  }

  if (!ad) {
    return (
      <section className="container-page section text-center">
        <h1 className="page-title">{t("brandPage.notFound")}</h1>
        <Link to="/markalar" className="btn-primary mt-8 inline-flex">
          {t("brandPage.allBrands")}
        </Link>
      </section>
    );
  }

  const kapak = markaUrunleri.filter((_, i) => i % Math.max(1, Math.floor(markaUrunleri.length / 6)) === 0).slice(0, 6);

  return (
    <main>
      {/* Üst bölüm */}
      <section className="border-b border-line bg-canvas/60">
        <div className="container-page grid gap-10 py-10 sm:py-14 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div className="text-start">
            <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-2 text-[13px] text-muted">
              <Link to="/markalar" className="hover:text-ink">{t("brandPage.indexTitle")}</Link>
              <span aria-hidden="true" className="text-line">/</span>
              <span className="font-medium text-ink">{ad}</span>
            </nav>
            <Amblem ad={ad} buyuk />
            <p className="mt-6 max-w-xl text-[15.5px] leading-relaxed text-body">{tanitim || otomatikTanitim}</p>
            {tanitim && <p className="mt-2 max-w-xl text-[13.5px] text-muted">{otomatikTanitim}</p>}
            <div className="mt-6 flex flex-wrap gap-2">
              {gruplar.map(([k, liste]) => (
                <a
                  key={k}
                  href={`#kat-${k}`}
                  className="rounded-full border border-line bg-white px-3.5 py-1.5 text-[12.5px] text-body transition-colors hover:border-ink/30 hover:text-ink"
                >
                  {kategoriAdi(adlar, k, lang)} <span className="tabular-nums text-muted">({liste.length})</span>
                </a>
              ))}
            </div>
          </div>
          {/* Markanın ürünlerinden kolaj */}
          <div className="grid grid-cols-3 gap-2">
            {kapak.map((u) => (
              <div key={u.id} className="relative aspect-square overflow-hidden rounded-xl border border-line bg-white">
                <img src={u.gorsel} alt="" className="absolute inset-0 h-full w-full object-contain p-3" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Kategorilere göre ürünler */}
      <div className="container-page space-y-14 py-12 sm:py-14">
        {gruplar.map(([k, liste]) => {
          const tumu = acik[k];
          const gorunen = tumu ? liste : liste.slice(0, GRUP_ILK);
          return (
            <section key={k} id={`kat-${k}`} className="scroll-mt-40">
              <div className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b border-line pb-3">
                <h2 className="text-[20px] font-semibold tracking-tight text-ink">
                  {kategoriAdi(adlar, k, lang)} <span className="text-[14px] font-normal text-muted">({liste.length})</span>
                </h2>
                <Link to={`/urunler/${k}`} className="link-arrow text-[13px]">
                  {t("brandPage.categoryAll")}
                  <span aria-hidden="true" className="arrow-forward" />
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {gorunen.map((u) => (
                  <ProductCard key={u.id} urun={u} altBilgi={kategoriAdi(adlar, u.kategori, lang)} />
                ))}
              </div>
              {liste.length > GRUP_ILK && (
                <div className="mt-5 text-center">
                  <button
                    type="button"
                    onClick={() => setAcik((o) => ({ ...o, [k]: !tumu }))}
                    className="rounded-md border border-line px-5 py-2.5 text-[13.5px] font-medium text-ink transition-colors hover:border-ink"
                  >
                    {tumu ? t("brandPage.showLess") : t("brandPage.showMore", { count: liste.length - GRUP_ILK })}
                  </button>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </main>
  );
}
